//-- copyright
// OpenProject Resource Planner (multi-resource) management plugin.
//++
//
// Client-side mirror of ResourcePlannerPlugin::CalendarResolver +
// TimephasedCalculator so the WP assignment grid preview and the Resource Usage
// heatmap match exactly what the backend persists. Keep the two in sync — the
// precedence is: exception (incl. recurring) → work week → base weekday →
// parent calendar (DEFAULT). Times are "HH:MM"; "24:00" is the midnight
// sentinel used by midnight-crossing shifts.

const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const ORDINALS:Record<string,number> = { FIRST: 1, SECOND: 2, THIRD: 3, FOURTH: 4 };
const MAX_PARENT_DEPTH = 10;
const MAX_OCCURRENCE_SCAN = 20000;

export interface TimeRange { from:string; to:string; }
export type DayType = 'WORKING'|'NON_WORKING'|'DEFAULT';
export interface WeekDay { day:string; type:DayType; hours:TimeRange[]; }
export interface WorkWeek { name?:string; from:string; to:string; weekDays:WeekDay[]; }
export interface RecurRange { kind:'NO_END'|'END_DATE'|'OCCURRENCES'; date?:string|null; count?:number; }
export interface Recurrence {
  type:'DAILY'|'WEEKLY'|'MONTHLY'|'YEARLY';
  every?:number; workingDaysOnly?:boolean; weekdays?:string[];
  relative?:boolean; ordinal?:string; weekday?:string; month?:number; day?:number;
  range:RecurRange;
}
export interface CalException {
  name?:string; from:string; to:string; working:boolean;
  hours?:TimeRange[]; recurring?:Recurrence|null;
}
export interface TpCalendar {
  schemaVersion?:number; guid?:string|null; type?:'PROJECT'|'RESOURCE'|'TASK';
  weekDays?:WeekDay[]; workWeeks?:WorkWeek[]; exceptions?:CalException[];
  parentCalendarId?:number|null;
  minutesPerDay?:number; minutesPerWeek?:number; daysPerMonth?:number;
  // legacy v1 keys (upgraded on the fly by normalizeCalendar):
  workingDays?:boolean[]; holidays?:{ date:string; name?:string|null }[];
  hoursStart?:string; hoursEnd?:string; hoursPerDay?:number;
}
export interface TpPeriod { date:string; hours:number; }
// id → calendar definition, used to resolve parentCalendarId inheritance.
export type CalLookup = (id:number) => TpCalendar | null | undefined;

export function parseISO(s:string):Date { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); }
export function isoDate(dt:Date):string {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
// Advance by calendar days, not fixed milliseconds: adding n*86400000 ms is
// wrong across a DST boundary (a 23h/25h local day would repeat or skip a
// date), which would make this preview diverge from the Date-based backend.
export function addDays(dt:Date, n:number):Date {
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + n);
}

// ── v1 → v2 normalisation (matches CalendarDefinition#normalize) ──────────
export function normalizeCalendar(cal:TpCalendar|undefined|null):TpCalendar {
  if (!cal) { return { weekDays: standardWeek(), workWeeks: [], exceptions: [] }; }
  if (Array.isArray(cal.weekDays) && cal.weekDays.length) { return cal; }

  // A stub with no day list at all (e.g. `{}` or just `{parentCalendarId}` from an
  // importer) is NOT a fully non-working calendar. Mirror CalendarDefinition#week_days:
  // a derived calendar defers every day to its base, a base calendar gets Mon–Fri.
  const legacyV1 = cal.workingDays != null || cal.hoursStart != null
    || cal.hoursEnd != null || cal.holidays != null;
  if (!legacyV1) {
    const weekDays:WeekDay[] = cal.parentCalendarId != null
      ? DAYS.map((day) => ({ day, type: 'DEFAULT' as DayType, hours: [] }))
      : standardWeek();
    return {
      ...cal, weekDays, workWeeks: cal.workWeeks || [], exceptions: cal.exceptions || [],
    };
  }

  // legacy v1 row
  const on = cal.workingDays && cal.workingDays.length === 7
    ? cal.workingDays : [false, true, true, true, true, true, false];
  const from = cal.hoursStart || '09:00';
  const to = cal.hoursEnd || '17:00';
  const weekDays:WeekDay[] = DAYS.map((day, i) => (on[i]
    ? { day, type: 'WORKING' as DayType, hours: [{ from, to }] }
    : { day, type: 'NON_WORKING' as DayType, hours: [] }));
  const exceptions:CalException[] = (cal.holidays || [])
    .filter((h) => h && h.date)
    .map((h) => ({ name: h.name || '', from: h.date, to: h.date, working: false }));
  return {
    weekDays, workWeeks: [], exceptions,
    minutesPerDay: (cal.hoursPerDay || 8) * 60,
    parentCalendarId: cal.parentCalendarId ?? null,
  };
}

function standardWeek():WeekDay[] {
  return DAYS.map((day, i) => (i >= 1 && i <= 5
    ? { day, type: 'WORKING' as DayType, hours: [{ from: '09:00', to: '17:00' }] }
    : { day, type: 'NON_WORKING' as DayType, hours: [] }));
}

// ── resolution ────────────────────────────────────────────────────────────
function toMin(s:string):number|null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s || '');
  if (!m) { return null; }
  return Number(m[1]) * 60 + Number(m[2]);
}
function minuteRanges(hours:TimeRange[]|undefined):Array<[number,number]> {
  const out:Array<[number,number]> = [];
  (hours || []).forEach((h) => {
    const a = toMin(h.from); const b = toMin(h.to);
    if (a != null && b != null && b > a) { out.push([a, b]); }
  });
  return out;
}
function findDay(weekDays:WeekDay[]|undefined, dt:Date):WeekDay|undefined {
  const name = DAYS[dt.getDay()];
  return (weekDays || []).find((wd) => (wd.day || '').toUpperCase() === name);
}
function weekStart(dt:Date):Date { return addDays(dt, -dt.getDay()); }
function monthsBetween(a:Date, b:Date):number { return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()); }
function daysBetween(a:Date, b:Date):number { return Math.round((b.getTime() - a.getTime()) / 86400000); }
function ordinalMatches(dt:Date, ordinal?:string):boolean {
  if ((ordinal || '').toUpperCase() === 'LAST') { return addDays(dt, 7).getMonth() !== dt.getMonth(); }
  const n = ORDINALS[(ordinal || '').toUpperCase()];
  return !!n && Math.floor((dt.getDate() - 1) / 7) + 1 === n;
}
function patternMatches(rec:Recurrence, anchor:Date, dt:Date):boolean {
  const every = Math.max(rec.every || 1, 1);
  switch (rec.type) {
    case 'DAILY':
      if (rec.workingDaysOnly && (dt.getDay() === 0 || dt.getDay() === 6)) { return false; }
      return daysBetween(anchor, dt) % every === 0;
    case 'WEEKLY': {
      if (!(rec.weekdays || []).map((d) => d.toUpperCase()).includes(DAYS[dt.getDay()])) { return false; }
      return Math.floor(daysBetween(weekStart(anchor), dt) / 7) % every === 0;
    }
    case 'MONTHLY':
      if (monthsBetween(anchor, dt) % every !== 0) { return false; }
      return rec.relative
        ? ordinalMatches(dt, rec.ordinal) && DAYS[dt.getDay()] === (rec.weekday || '').toUpperCase()
        : dt.getDate() === Number(rec.day);
    case 'YEARLY':
      if (dt.getMonth() + 1 !== Number(rec.month)) { return false; }
      return rec.relative
        ? ordinalMatches(dt, rec.ordinal) && DAYS[dt.getDay()] === (rec.weekday || '').toUpperCase()
        : dt.getDate() === Number(rec.day);
    default: return false;
  }
}
function occurrenceIndex(rec:Recurrence, anchor:Date, dt:Date):number|null {
  if (!patternMatches(rec, anchor, dt)) { return null; }
  let idx = 0; let d = anchor; let scanned = 0;
  while (d <= dt && scanned < MAX_OCCURRENCE_SCAN) {
    if (patternMatches(rec, anchor, d)) { idx += 1; }
    if (isoDate(d) === isoDate(dt)) { return idx; }
    d = addDays(d, 1); scanned += 1;
  }
  return null;
}
function recurrenceMatches(ex:CalException, dt:Date):boolean {
  const rec = ex.recurring as Recurrence;
  const anchor = parseISO(ex.from);
  if (dt < anchor) { return false; }
  const range = rec.range || { kind: 'NO_END' };
  if (range.kind === 'END_DATE' && range.date && dt > parseISO(range.date)) { return false; }
  if (range.kind === 'OCCURRENCES') {
    const idx = occurrenceIndex(rec, anchor, dt);
    return !!idx && idx <= (range.count || 0);
  }
  return patternMatches(rec, anchor, dt);
}
function exceptionCovers(ex:CalException, dt:Date):boolean {
  if (ex.recurring) { return recurrenceMatches(ex, dt); }
  const f = parseISO(ex.from); const t = parseISO(ex.to || ex.from);
  return dt >= f && dt <= t;
}

// Working minute-ranges on a date, applying full MSP precedence. `lookup`
// resolves parentCalendarId inheritance; `depth` guards parent cycles.
export function rangesOn(cal:TpCalendar, dt:Date, lookup?:CalLookup, depth = 0):Array<[number,number]> {
  const def = normalizeCalendar(cal);
  const ex = (def.exceptions || []).find((e) => exceptionCovers(e, dt));
  if (ex) {
    if (!ex.working) { return []; }
    const h = minuteRanges(ex.hours);
    return h.length ? h : weekdayRanges(def, dt, lookup, depth);
  }
  const ww = (def.workWeeks || []).find((w) => dt >= parseISO(w.from) && dt <= parseISO(w.to));
  if (ww) {
    const wd = findDay(ww.weekDays, dt);
    if (wd) {
      if (wd.type === 'WORKING') { return minuteRanges(wd.hours); }
      if (wd.type === 'NON_WORKING') { return []; }
      // DEFAULT → fall through to base weekday
    }
  }
  return weekdayRanges(def, dt, lookup, depth);
}
function weekdayRanges(def:TpCalendar, dt:Date, lookup?:CalLookup, depth = 0):Array<[number,number]> {
  const wd = findDay(def.weekDays, dt);
  if (!wd) { return []; }
  if (wd.type === 'WORKING') { return minuteRanges(wd.hours); }
  if (wd.type === 'DEFAULT' && lookup && def.parentCalendarId != null && depth < MAX_PARENT_DEPTH) {
    const parent = lookup(def.parentCalendarId);
    if (parent) { return rangesOn(parent, dt, lookup, depth + 1); }
  }
  return [];
}

export function isWorkingDay(cal:TpCalendar|undefined|null, dt:Date, lookup?:CalLookup):boolean {
  if (!cal) { return dt.getDay() !== 0 && dt.getDay() !== 6; }
  return rangesOn(cal, dt, lookup).length > 0;
}
export function workingMinutesOn(cal:TpCalendar|undefined|null, dt:Date, lookup?:CalLookup):number {
  if (!cal) { return 0; }
  return rangesOn(cal, dt, lookup).reduce((sum, [a, b]) => sum + (b - a), 0);
}
export function workingHoursOn(cal:TpCalendar|undefined|null, dt:Date, lookup?:CalLookup):number {
  return workingMinutesOn(cal, dt, lookup) / 60;
}
export function isHoliday(cal:TpCalendar|undefined|null, dt:Date):boolean {
  if (!cal) { return false; }
  const def = normalizeCalendar(cal);
  return (def.exceptions || []).some((e) => !e.working && exceptionCovers(e, dt));
}

// Working-day Date objects in [startISO, finishISO] for the calendar.
export function workingDays(cal:TpCalendar|undefined|null, startISO:string|null, finishISO:string|null, lookup?:CalLookup):Date[] {
  if (!startISO || !finishISO) { return []; }
  const start = parseISO(startISO); const finish = parseISO(finishISO);
  if (finish < start) { return []; }
  const out:Date[] = [];
  for (let dt = start; dt <= finish; dt = addDays(dt, 1)) { if (isWorkingDay(cal, dt, lookup)) { out.push(new Date(dt)); } }
  return out;
}

// Contour weights (index 0 = first day). Mirrors the Ruby implementation.
export function contourWeights(contour:string, n:number):number[] {
  if (n <= 0) { return []; }
  if (n === 1) { return [1]; }
  const out:number[] = [];
  const center = (n - 1) / 2;
  const peak = center + 1;
  const ramp = Math.max(Math.ceil(n / 4), 1);
  for (let i = 0; i < n; i += 1) {
    switch (contour) {
      case 'front': out.push(n - i); break;
      case 'back':  out.push(i + 1); break;
      case 'bell':  out.push(Math.min(Math.max(peak - Math.abs(i - center), 1), peak)); break;
      case 'turtle': out.push(Math.max(Math.min(i + 1, n - i, ramp), 1)); break;
      default: out.push(1);
    }
  }
  return out;
}

const round2 = (v:number):number => Math.round(v * 100) / 100;

// Spread total hours across working days by contour; residue folded into last day.
export function distribute(totalHours:number, days:Date[], contour:string):TpPeriod[] {
  if (!days.length) { return []; }
  const weights = contourWeights(contour, days.length);
  let sum = weights.reduce((a, b) => a + b, 0);
  if (sum === 0) { sum = days.length; }
  let allocated = 0;
  return days.map((d, i) => {
    let hours:number;
    if (i === days.length - 1) { hours = Math.max(0, round2(totalHours - allocated)); } else { hours = round2((weights[i] / sum) * totalHours); allocated += hours; }
    return { date: isoDate(d), hours };
  });
}

export function buildTimephased(
  cal:TpCalendar|undefined|null, startISO:string|null, finishISO:string|null, totalHours:number, contour:string, lookup?:CalLookup,
):TpPeriod[] {
  return distribute(totalHours || 0, workingDays(cal, startISO, finishISO, lookup), contour);
}
